import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import gsap from 'gsap';
import { Menu, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import CallbackForm from '../ui/CallbackForm';

const BrutalistNavbar = () => {
  const navRef = useRef(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [isScrolled, setIsScrolled] = useState(false);
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 50;
      setIsScrolled(scrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (isScrolled && !isMenuOpen) {
      gsap.to(navRef.current, {
        backgroundColor: 'rgba(245, 243, 238, 0.6)',
        backdropFilter: 'blur(24px)',
        border: '1px solid rgba(17, 17, 17, 1)',
        duration: 0.4,
        ease: 'power2.out',
        width: 'auto',
        borderRadius: '9999px',
        padding: '0.75rem 1.5rem',
        marginTop: '1.5rem'
      });
    } else {
      gsap.to(navRef.current, {
        backgroundColor: 'rgba(245, 243, 238, 0)',
        backdropFilter: 'blur(0px)',
        border: '1px solid rgba(17, 17, 17, 0)',
        duration: 0.4,
        ease: 'power2.out',
        width: '100%',
        borderRadius: '0px',
        padding: '1.5rem 2rem',
        marginTop: '0'
      });
    }
  }, [isScrolled, isMenuOpen]);

  const links = [
    { name: 'Боксы', path: '/catalog' },
    { name: 'Тарифы', path: '/tariffs' },
    { name: 'Как это работает', path: '/how-it-works' },
    { name: 'Контакты', path: '/contacts' },
    { name: 'Кабинет', path: typeof window !== 'undefined' && localStorage.getItem('kladoffka24:token') ? '/cabinet' : '/login' },
  ];

  return (
    <>
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 bg-background/95 backdrop-blur-lg pt-24 px-6 md:hidden flex flex-col items-center justify-start space-y-6 overflow-y-auto h-[100dvh]"
          >
            {links.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                className="text-xl font-bold uppercase tracking-widest hover:text-accent transition-colors text-foreground"
              >
                {link.name}
              </Link>
            ))}
            <button
              onClick={() => {
                setIsMenuOpen(false);
                setIsCallbackOpen(true);
              }}
              className="bg-accent text-accent-foreground px-8 py-3 text-base font-mono uppercase tracking-wider hover:bg-accent/90 transition-colors w-full max-w-xs"
            >
              Забронировать
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="fixed top-0 left-0 right-0 z-50 flex justify-center pointer-events-none">
        <nav
          ref={navRef}
          className="pointer-events-auto flex items-center justify-between gap-4 md:gap-8 w-full"
        >
          <Link 
            to="/" 
            className={`flex flex-col items-center justify-center transition-colors z-50 group ${
              isHomePage && !isScrolled && !isMenuOpen ? 'text-white' : 'text-foreground'
            }`}
            onClick={() => setIsMenuOpen(false)}
          >
            <img 
              src="/images/logo.png" 
              alt="Кладоффка24"
              className={`h-24 w-auto mb-1 transition-all duration-300 ${
                isHomePage && !isScrolled && !isMenuOpen ? 'brightness-0 invert' : ''
              }`}
            />

          </Link>

          <div className="hidden md:flex items-center gap-6">
            {links.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`font-mono text-sm uppercase tracking-wide transition-colors ${
                  isHomePage && !isScrolled 
                    ? 'text-white/90 hover:text-white' 
                    : 'text-foreground/80 hover:text-accent'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsCallbackOpen(true)}
              className="hidden md:block bg-accent text-accent-foreground px-6 py-2 font-mono text-sm uppercase tracking-wider hover:bg-accent/90 transition-colors"
            >
              Забронировать
            </button>

            <button
              className={`md:hidden p-2 transition-colors z-50 ${
                isHomePage && !isScrolled && !isMenuOpen ? 'text-white' : 'text-foreground'
              }`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </nav>
      </div>

      <CallbackForm
        isOpen={isCallbackOpen}
        onClose={() => setIsCallbackOpen(false)}
      />
    </>
  );
};

export default BrutalistNavbar;
